import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';

import { Grid } from '@material-ui/core';

import { FetchError } from '../../../common/api/api';
import AppSnackbar from '../../../common/AppSnackbar';
import TaskAttachmentCard from '../../tasks/cards/TaskAttachmentCard';
import { Task } from '../../tasks/Task.interface';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import UserNeedDesignInputReferenceCard from '../cards/UserNeedDesignInputReferenceCard';
import UserNeedInfoCard from '../cards/UserNeedInfoCard';
import {
  useFindProduct,
  useFindProductUserNeeds,
  useProductFilterQueryParams
} from '../Product.hooks';
import {
  BaseProductUserNeedsCardProps,
  ProductEntity,
  ProductUserNeed
} from '../Product.interface';
import ProductDocumentCommonPage from './ProductDocumentCommonPage';
import { getProductEntityName } from '../Product.config';

const ProductUserNeedDocumentPage: React.FC = () => {
  const { productId: paramProductId = '' } = useParams<{
    productId?: string;
  }>();
  const productId: number = parseInt(paramProductId) || 0;
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const [search = '', setSearch] = useQueryParam('search', StringParam);
  const [productUserNeeds, setProductUserNeeds] = useState<ProductUserNeed[]>(
    []
  );
  const {
    productUserNeedList,
    loadingProductUserNeedsError,
    findProductUserNeeds
  } = useFindProductUserNeeds(productId);
  const { product } = useFindProduct(productId, true);
  const { getParams } = useProductFilterQueryParams();

  const createCardProps = (
    productEntity: ProductEntity
  ): BaseProductUserNeedsCardProps => {
    const userNeed: ProductUserNeed = productEntity as ProductUserNeed;
    return {
      productId,
      productUserNeed: userNeed,
      onUpdate: () => setDidUpdate(true),
      onError: (error: FetchError) => setError(error),
      onChange: (newProductUserNeed: Partial<ProductUserNeed>) => {
        const selectedIndex: number = productUserNeeds.findIndex(
          (un: ProductUserNeed) => un.id === userNeed.id
        );
        if (selectedIndex > -1) {
          const userNeeds: ProductUserNeed[] = [...productUserNeeds];
          userNeeds[selectedIndex] = { ...userNeed, ...newProductUserNeed };
          setProductUserNeeds(userNeeds);
        }
      }
    };
  };

  useEffect(() => {
    findProductUserNeeds({ search, ...getParams() });
  }, [findProductUserNeeds, search, getParams]);

  useEffect(() => {
    if (productUserNeedList) {
      setProductUserNeeds(productUserNeedList.data);
    }
  }, [productUserNeedList]);

  useEffect(() => {
    setError(loadingProductUserNeedsError);
  }, [loadingProductUserNeedsError]);

  return (
    <>
      <ProductDocumentCommonPage
        product={product}
        productEntities={productUserNeeds}
        entity={ProductBoardColumnEntity.UserNeed}
        renderInfoCard={(
          productEntity: ProductEntity,
          title: string,
          hidden?: boolean
        ) => (
          <UserNeedInfoCard
            {...createCardProps(productEntity)}
            title={title}
            hidden={hidden}
            enableTitleRedirect
          />
        )}
        renderGridCards={(productEntity: ProductEntity) => {
          const props: BaseProductUserNeedsCardProps = createCardProps(
            productEntity
          );
          return (
            <>
              <Grid item xs={12}>
                <UserNeedDesignInputReferenceCard {...props} />
              </Grid>
              {productEntity.task && (
                <Grid item xs={12}>
                  <TaskAttachmentCard
                    compact
                    task={productEntity.task}
                    onError={props.onError}
                    onChange={(newTask: Partial<Task>) => {
                      Object.assign(productEntity.task, newTask);
                      // update with new task
                      props.onChange?.({});
                      props.onUpdate?.();
                    }}
                  />
                </Grid>
              )}
            </>
          );
        }}
      />

      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        Product {getProductEntityName('userNeed')} details updated.
      </AppSnackbar>
    </>
  );
};

export default ProductUserNeedDocumentPage;
